import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { IChangeReview, isFieldDiff } from "../../../../../types/review";
import ContactPersonDetails from "./ContactPersonDetails";
import ServicePricesDetails from "./ServicePricesDetails";
import CarWashDetailsCard from "./CarWashDetailsCard";
import LocationMapModal from "../../../ManageCarWashes/components/LocationMapModal";

interface ReviewDetailsModalProps {
  open: boolean;
  review: IChangeReview | null;
  onClose: () => void;
  // Only shown when the review is pending and the handler is provided.
  onApprove?: (reviewId: string) => void | Promise<void>;
  onReject?: (reviewId: string, comment: string) => void | Promise<void>;
  actionLoading?: boolean;
}

const REQUEST_TYPE_LABEL_KEYS: Record<string, string> = {
  carWash: "reviews.typeNewCarWash",
  contactPerson: "reviews.typeContactPersonChange",
  price: "reviews.typePriceChange",
};

const REVIEW_STATUS_LABEL_KEYS: Record<string, string> = {
  pending: "reviews.statusPending",
  approved: "reviews.statusApproved",
  rejected: "reviews.statusRejected",
};

const getRequestTypeIcon = (type: string) => {
  switch (type) {
    case "carWash":
      return <BusinessIcon />;
    case "contactPerson":
      return <PersonIcon />;
    case "price":
      return <MoneyIcon />;
    default:
      return <PendingIcon />;
  }
};

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

// Collapse a possibly-diffed value to what is being PROPOSED.
const proposed = (value: unknown) =>
  isFieldDiff(value) ? (value as { new: unknown }).new : value;

const formatDate = (t: TFunc, date?: Date | string) => {
  if (!date) return t("common.notAvailable");
  return new Date(date).toLocaleString();
};

// carWash is legitimately null for create-type reviews (requestType "carWash"):
// per Phase 34-01 the car wash record is only created once the RM approves, so
// the proposed name lives in changes.legalName until then — fall back to it
// before showing the "unknown" placeholder.
const getCarWashName = (
  t: TFunc,
  carWash: IChangeReview["carWash"],
  changes?: IChangeReview["changes"]
) => {
  if (carWash) {
    if (typeof carWash === "string") return carWash;
    return carWash.legalName || carWash._id || t("common.unknown");
  }
  const proposedName = changes?.legalName;
  if (typeof proposedName === "string" && proposedName) return proposedName;
  return t("common.notAvailable");
};

const getOwnerName = (t: TFunc, owner: IChangeReview["owner"]) => {
  if (!owner || typeof owner === "string") return t("common.unknown");
  return (
    `${owner.firstName || ""} ${owner.lastName || ""}`.trim() ||
    t("common.unknown")
  );
};

// Shared, responsive "Review Details" modal used everywhere a car-wash change
// request is inspected (per-carwash pending-reviews tab + RM pending list).
// Old→new diffs are rendered side-by-side in two colors by the detail cards
// (FieldDiff): red strikethrough for the old value, green for the new one.
const ReviewDetailsModal = ({
  open,
  review,
  onClose,
  onApprove,
  onReject,
  actionLoading,
}: ReviewDetailsModalProps) => {
  const { t } = useTranslation();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [mapOpen, setMapOpen] = useState(false);

  if (!review) return null;

  const requestTypeLabel = REQUEST_TYPE_LABEL_KEYS[review.requestType]
    ? t(REQUEST_TYPE_LABEL_KEYS[review.requestType])
    : review.requestType;
  const reviewStatusLabel = REVIEW_STATUS_LABEL_KEYS[review.status]
    ? t(REVIEW_STATUS_LABEL_KEYS[review.status])
    : review.status;

  const isPending = review.status === "pending";
  const canApprove = isPending && !!onApprove;
  const canReject = isPending && !!onReject;
  const changesLocation = review.changes?.location;

  // CarWashDetailsCard renders every section (name, parking, location, contact,
  // images, prices), so it is the right renderer whenever the review carries
  // anything beyond contact/prices — which is also every merged review.
  const c = review.changes ?? {};
  const isCarWashShaped =
    review.requestType === "carWash" ||
    Boolean(c.legalName || c.location || c.images || c.parkingDescription);

  const handleApprove = () => {
    if (review._id && onApprove) onApprove(review._id);
    onClose();
  };

  const handleRejectConfirm = () => {
    if (review._id && onReject) onReject(review._id, rejectComment);
    setRejectOpen(false);
    setRejectComment("");
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {getRequestTypeIcon(review.requestType)}
            {t("reviews.detailsTitle", { type: requestTypeLabel })}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("reviews.requestType")}
              </Typography>
              <Typography variant="body1">{requestTypeLabel}</Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("common.status")}
              </Typography>
              <Chip
                label={reviewStatusLabel}
                color={
                  review.status === "pending"
                    ? "warning"
                    : review.status === "approved"
                    ? "success"
                    : "error"
                }
                size="small"
              />
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("reviews.colOwner")}
              </Typography>
              <Typography variant="body1">
                {getOwnerName(t, review.owner)}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("reviews.colCarWash")}
              </Typography>
              <Typography variant="body1">
                {getCarWashName(t, review.carWash, review.changes)}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("reviews.createdAt")}
              </Typography>
              <Typography variant="body1">
                {formatDate(t, review.createdAt)}
              </Typography>
            </Grid>

            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                {t("reviews.changes")}
              </Typography>
            </Grid>

            {/* Driven by what `changes` CONTAINS, not by requestType.
                A review can now carry several kinds of change at once — an
                Owner revising an open request merges, say, prices and a rename
                into one — and branching on requestType alone rendered only the
                matching section, hiding the rest from the reviewer. An
                unrecognised requestType rendered nothing whatsoever. */}
            {isCarWashShaped ? (
              <CarWashDetailsCard
                changes={review.changes}
                onViewMap={
                  changesLocation ? () => setMapOpen(true) : undefined
                }
              />
            ) : (
              <>
                {review.changes.contact && (
                  <ContactPersonDetails contact={review.changes.contact} />
                )}
                {review.changes.services && (
                  <ServicePricesDetails
                    services={review.changes.services}
                    title={t("reviews.servicePriceChanges")}
                  />
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("common.close")}</Button>
          {canApprove && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleApprove}
              disabled={actionLoading}
            >
              {t("common.approve")}
            </Button>
          )}
          {canReject && (
            <Button
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setRejectOpen(true)}
              disabled={actionLoading}
            >
              {t("common.reject")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("reviews.rejectReview")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("reviews.rejectReasonHelp")}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t("reviews.rejectionReason")}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder={t("reviews.rejectionReasonPlaceholder")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleRejectConfirm}
            disabled={actionLoading || !rejectComment.trim()}
          >
            {t("reviews.confirmRejection")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location map. An edit records each location field as {old, new}, so
          resolve to the PROPOSED value — the map should show where the car
          wash is moving to, which is what the admin is being asked to approve. */}
      <LocationMapModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        latitude={proposed(changesLocation?.latitude) as string | undefined}
        longitude={proposed(changesLocation?.longitude) as string | undefined}
        title={getCarWashName(t, review.carWash, review.changes)}
        address={proposed(changesLocation?.street) as string | undefined}
      />
    </>
  );
};

export default ReviewDetailsModal;
